import type {
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    Skeleton,
} from "@mrflark/ui";
import {
    useQuery,
} from "@tanstack/react-query";
import type {
    ReactElement,
} from "preact/compat";

import {
    getPrimaryAnalysis,
} from "@mrflark/spl_sdk";

import {
    splClient,
} from "../../api/client.ts";

type SimilarItemsPanelProps = {
    currentItem: MediaItem | null;
    onNavigateToItem: (item: MediaItem) => void;
};

export const SimilarItemsPanel = ({
    currentItem,
    onNavigateToItem,
}: SimilarItemsPanelProps): ReactElement | null => {
    const hasAiData = !!currentItem?.ai_analyses &&
        Object.keys(currentItem.ai_analyses).length > 0;

    const {
        data,
        isLoading,
    } = useQuery({
        queryKey: ["similarMedia", currentItem?.id],
        queryFn: () =>
            splClient.getSimilar(currentItem?.id ?? "", {
                limit: 12,
            }),
        enabled: hasAiData && !!currentItem?.id,
        staleTime: 5 * 60 * 1000,
    });

    if (!hasAiData || (!isLoading && (!data || data.items.length === 0))) {
        return null;
    }

    return (
        <div className="mt-4 w-full max-w-3xl">
            <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
                More Like This
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
                {isLoading
                    ? (
                        Array.from({
                            length: 8,
                        }).map((_, index) => (
                            <Skeleton
                                key={index}
                                className="w-24 h-24 flex-shrink-0 rounded-lg"
                            />
                        ))
                    )
                    : (
                        data?.items.map((item) => (
                            <button
                                type="button"
                                key={item.id}
                                onClick={() => onNavigateToItem(item)}
                                className="flex-shrink-0 group relative"
                                title={getPrimaryAnalysis(item.ai_analyses)
                                    ?.entry.description || item.id}
                            >
                                {item.media_type === "video"
                                    ? (
                                        <video
                                            src={item.thumbnail_url ||
                                                item.media_url}
                                            className="w-24 h-24 object-cover rounded-lg border border-border group-hover:border-accent transition-colors"
                                            muted
                                        />
                                    )
                                    : (
                                        <img
                                            src={item.thumbnail_url ||
                                                item.media_url}
                                            alt=""
                                            className="w-24 h-24 object-cover rounded-lg border border-border group-hover:border-accent transition-colors"
                                            loading="lazy"
                                        />
                                    )}
                            </button>
                        ))
                    )}
            </div>
        </div>
    );
};
